/**
 * THE FIXTURE DESK'S VIDEOS (v1.0.29) - the list rows, the working copies and the video behind each,
 * shared by ./fixture-cabinet-api.ts (the Videos list, "Upload video") and ./fixture-editor-api.ts
 * (the video editor), so the two fixtures describe one author's desk.
 *
 * It behaves as the platform does where a screen can tell: a link that is not one YouTube video and
 * a file over the limit are refused; an upload is `uploaded`, then `processing`, then `ready` (or
 * `failed` with the code the desk was built with) - one step per `getArticleVideo` read, so a poll
 * walks it through; a frame or the YouTube still becomes a cover that keeps the video's shape.
 *
 * A draft "Upload video" makes on the cabinet's desk is named `FIXTURE_NEW_VIDEO_PREFIX` plus a
 * number, and the editor's desk opens any such id as a new empty video draft: the two fixtures hold
 * separate state, and without this the new draft could not be opened in the default build. A live
 * stream "Live stream" makes is named `FIXTURE_NEW_STREAM_PREFIX` and opens as a new stream draft.
 */
import { articleWordCount } from './editor-measure';
import { VIDEO_MAX_BYTES } from './editor-api';
import { ApiProblem } from './problem';
import type {
  ArticleVideo,
  ArticleVideoRequest,
  Block,
  StoredImage,
  StructuralRules,
  VideoCoverRequest,
  VideoFailureCode,
  WorkingCopy,
} from './editor-api';
import type { ArticleListItem, ErrorCode } from './generated';

/**
 * The desk's videos: a new draft, a ready vertical upload, a YouTube video in review, and (1.2.0)
 * a live stream draft with its link and its planned times.
 */
export const FIXTURE_VIDEO_IDS = {
  draft: 'vid_0192f7a1',
  ready: 'vid_0192f7b2',
  inReview: 'vid_0192f7c3',
  stream: 'vid_0192f7d4',
} as const;

/** The id stem of a video draft "Upload video" makes on the fixture desk. */
export const FIXTURE_NEW_VIDEO_PREFIX = 'vid_fixture_';

/**
 * The id stem of a live stream draft "Live stream" makes. It begins with the video stem, so the
 * editor's desk tells the two apart by this one first.
 */
export const FIXTURE_NEW_STREAM_PREFIX = `${FIXTURE_NEW_VIDEO_PREFIX}live_`;

const SAVED_AT = '2026-08-23T17:30:00Z';

/** The seeded stream's planned times: after the desk's own now (`FIXTURE_PAYMENTS.computedAt`). */
const STREAM_TIMES = { startsAt: '2026-09-05T18:00:00Z', endsAt: '2026-09-05T19:30:00Z' } as const;

export const FIXTURE_VIDEOS: readonly ArticleListItem[] = [
  {
    articleId: FIXTURE_VIDEO_IDS.stream,
    status: 'draft',
    title: 'New Moon in Virgo, live',
    subtitle: 'Setting intentions together, questions answered on air',
    awaitingCover: true,
    streamStartsAt: STREAM_TIMES.startsAt,
    updatedAt: '2026-08-24T08:15:00Z',
  },
  {
    articleId: FIXTURE_VIDEO_IDS.ready,
    status: 'draft',
    title: 'Three breaths before a tarot reading',
    subtitle: 'A one-minute ritual to settle before you shuffle',
    awaitingCover: true,
    updatedAt: SAVED_AT,
  },
  {
    articleId: FIXTURE_VIDEO_IDS.inReview,
    status: 'in_review',
    title: 'Full Moon ritual, live',
    subtitle: 'What to release under the Aquarius Full Moon',
    coverUrl: 'https://i.ytimg.com/vi/M7lc1UVf-VE/hqdefault.jpg',
    updatedAt: '2026-08-22T09:10:00Z',
  },
  {
    articleId: FIXTURE_VIDEO_IDS.draft,
    status: 'draft',
    title: 'Full Moon in Aquarius: what to release',
    awaitingCover: true,
    updatedAt: '2026-08-21T19:45:00Z',
  },
];

function counted(copy: Omit<WorkingCopy, 'wordCount' | 'blockCount'>): WorkingCopy {
  return { ...copy, wordCount: articleWordCount(copy), blockCount: copy.blocks.length };
}

function paragraph(blockId: string, body: string): Block {
  return { blockId, kind: 'text', body };
}

/** A new, empty video draft - what `createVideoDraft` answers. */
export function emptyVideoDraft(articleId: string, updatedAt: string): WorkingCopy {
  return counted({
    articleId,
    kind: 'video',
    video: null,
    stream: null,
    status: 'draft',
    revision: 0,
    title: '',
    subtitle: '',
    cover: null,
    seoTitle: null,
    metaDescription: null,
    slug: null,
    sourceLanguage: 'en',
    articleFormat: null,
    timeSensitivity: null,
    plannedGoLiveAt: null,
    categories: [],
    addressCategory: null,
    tags: [],
    blocks: [],
    awaitingCover: true,
    writableBy: 'author',
    updatedAt,
  });
}

/** A new, empty live stream draft - what `createVideoDraft({ stream: true })` answers. */
export function emptyStreamDraft(articleId: string, updatedAt: string): WorkingCopy {
  return { ...emptyVideoDraft(articleId, updatedAt), stream: { startsAt: null, endsAt: null } };
}

const YOUTUBE_COVER: StoredImage = {
  fileId: 'img_vid_0192f7c3',
  url: 'https://i.ytimg.com/vi/M7lc1UVf-VE/hqdefault.jpg',
  widthPx: 1_280,
  heightPx: 720,
  byteSize: 96_400,
  contentType: 'image/jpeg',
};

/** The working copies, keyed by id. The video itself is the desk's (`createFixtureVideoDesk`). */
export function seededVideoCopies(): ReadonlyMap<string, WorkingCopy> {
  const draft = emptyVideoDraft(FIXTURE_VIDEO_IDS.draft, '2026-08-21T19:45:00Z');
  return new Map<string, WorkingCopy>([
    [
      FIXTURE_VIDEO_IDS.draft,
      counted({
        ...draft,
        revision: 1,
        title: 'Full Moon in Aquarius: what to release',
        blocks: [
          paragraph(
            '0192f7a1-0001-7b01-9b01-000000000001',
            'A short ritual for the Full Moon: one thing to put down, and why you picked it up.',
          ),
        ],
      }),
    ],
    [
      FIXTURE_VIDEO_IDS.ready,
      counted({
        ...emptyVideoDraft(FIXTURE_VIDEO_IDS.ready, SAVED_AT),
        revision: 4,
        title: 'Three breaths before a tarot reading',
        subtitle: 'A one-minute ritual to settle before you shuffle',
        categories: ['tarot'],
        addressCategory: 'tarot',
        tags: ['tarot spreads'],
        blocks: [
          paragraph(
            '0192f7b2-0001-7b01-9b01-000000000001',
            'Before the cards, the breath. Three slow breaths, one question, and only then the shuffle.',
          ),
        ],
      }),
    ],
    [
      FIXTURE_VIDEO_IDS.inReview,
      counted({
        ...emptyVideoDraft(FIXTURE_VIDEO_IDS.inReview, '2026-08-22T09:10:00Z'),
        status: 'in_review',
        writableBy: 'nobody',
        revision: 6,
        title: 'Full Moon ritual, live',
        subtitle: 'What to release under the Aquarius Full Moon',
        cover: YOUTUBE_COVER,
        awaitingCover: false,
        categories: ['ritualsPractice'],
        addressCategory: 'ritualsPractice',
        tags: ['full moon'],
        blocks: [
          paragraph(
            '0192f7c3-0001-7b01-9b01-000000000001',
            'Recorded live under the Aquarius Full Moon. The ritual starts at 1:20.',
          ),
        ],
      }),
    ],
    [
      FIXTURE_VIDEO_IDS.stream,
      counted({
        ...emptyStreamDraft(FIXTURE_VIDEO_IDS.stream, '2026-08-24T08:15:00Z'),
        revision: 2,
        title: 'New Moon in Virgo, live',
        subtitle: 'Setting intentions together, questions answered on air',
        stream: { ...STREAM_TIMES },
        categories: ['ritualsPractice'],
        addressCategory: 'ritualsPractice',
        tags: ['new moon'],
        blocks: [
          paragraph(
            '0192f7d4-0001-7b01-9b01-000000000001',
            'Bring a notebook: we write three intentions together, then I answer your questions.',
          ),
        ],
      }),
    ],
  ]);
}

/**
 * The limits a video is held to, from the article's: no word floor and no picture rule - its
 * picture is the video (the server's `limitsForKind`). Every number stays the policy's.
 */
export function videoRulesOf(rules: StructuralRules): StructuralRules {
  return {
    ...rules,
    rules: rules.rules.flatMap((rule) => {
      if (rule.rule === 'image_present') {
        return [];
      }
      if (rule.rule === 'word_count') {
        return rule.max === undefined || rule.max === null ? [] : [{ ...rule, min: null }];
      }
      return [rule];
    }),
  };
}

/* ---------------------------------------------------------------------------------------------
 * The videos.
 * ------------------------------------------------------------------------------------------- */

export interface FixtureVideoDeskOptions {
  /** Every conversion on this desk ends `failed` with this code, so the refusal is renderable. */
  readonly failWith?: VideoFailureCode;
}

export interface FixtureVideoDesk {
  /** The video as `WorkingCopy.video` carries it: no addresses. Null when there is none. */
  current(articleId: string): ArticleVideo | null;
  /** `getArticleVideo`: walks a conversion one step, then answers with addresses once ready. */
  read(articleId: string): ArticleVideo;
  set(articleId: string, body: ArticleVideoRequest): ArticleVideo;
  remove(articleId: string): void;
  /** The cover a frame or the YouTube still makes, in the video's shape. */
  cover(articleId: string, body: VideoCoverRequest): StoredImage;
}

function refusal(status: number, errorCode: ErrorCode, detail: string): ApiProblem {
  return new ApiProblem({
    type: `https://moonx.art/problems/${errorCode.toLowerCase().replaceAll('_', '-')}`,
    title: 'The request cannot be processed.',
    status,
    error_code: errorCode,
    detail,
  });
}

const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/u;
const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
]);

/**
 * The server's reading of a YouTube link (`parseYoutubeLink`, src/modules/articles/video/
 * youtube-link.ts), rule for rule - its hosts, its http(s)-only scheme and its id - so the desk
 * takes and refuses exactly the links the platform does.
 */
export function youtubeLink(
  input: string,
): { readonly videoId: string; readonly isShort: boolean } | null {
  let url: URL;
  try {
    const trimmed = input.trim();
    url = new URL(/^[a-z]+:\/\//iu.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return null;
  }
  const host = url.hostname.toLowerCase();
  const segments = url.pathname.split('/').filter((part) => part !== '');
  let candidate: string | undefined;
  let isShort = false;
  if (host === 'youtu.be' || host === 'www.youtu.be') {
    candidate = segments[0];
  } else if (YOUTUBE_HOSTS.has(host)) {
    if (segments[0] === 'watch') {
      candidate = url.searchParams.get('v') ?? undefined;
    } else if (segments[0] === 'shorts' || segments[0] === 'embed' || segments[0] === 'live') {
      candidate = segments[1];
      isShort = segments[0] === 'shorts';
    }
  }
  return candidate !== undefined && VIDEO_ID.test(candidate)
    ? { videoId: candidate, isShort }
    : null;
}

const FRAME_COUNT = 8;

/**
 * A stand-in frame: a dark picture with a moon that moves along the strip, so eight frames look
 * like eight moments. Named colours only - it is a placeholder, not a themed surface.
 */
function frameUrl(index: number, vertical: boolean): string {
  const [width, height] = vertical ? [90, 160] : [160, 90];
  const x = Math.round(((index + 1) * width) / (FRAME_COUNT + 1));
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${String(width)}' height='${String(height)}'>` +
    `<rect width='100%' height='100%' fill='black'/>` +
    `<circle cx='${String(x)}' cy='${String(Math.round(height / 2))}' r='12' fill='white' fill-opacity='0.8'/>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

interface Held {
  readonly video: ArticleVideo;
}

export function createFixtureVideoDesk(options: FixtureVideoDeskOptions = {}): FixtureVideoDesk {
  const held = new Map<string, Held>([
    [
      FIXTURE_VIDEO_IDS.ready,
      {
        video: {
          source: 'upload',
          state: 'ready',
          failureCode: null,
          youtubeVideoId: null,
          orientation: 'vertical',
          durationMs: 74_000,
          widthPx: 1_080,
          heightPx: 1_920,
          fileName: 'three-breaths-vertical.mov',
          byteSize: 214_000_000,
        },
      },
    ],
    [
      FIXTURE_VIDEO_IDS.inReview,
      {
        video: {
          source: 'youtube',
          state: 'ready',
          failureCode: null,
          youtubeVideoId: 'M7lc1UVf-VE',
          orientation: 'horizontal',
          durationMs: null,
          widthPx: null,
          heightPx: null,
          fileName: null,
          byteSize: null,
        },
      },
    ],
    [
      FIXTURE_VIDEO_IDS.stream,
      {
        video: {
          source: 'youtube',
          state: 'ready',
          failureCode: null,
          youtubeVideoId: 'M7lc1UVf-VE',
          orientation: 'horizontal',
          durationMs: null,
          widthPx: null,
          heightPx: null,
          fileName: null,
          byteSize: null,
        },
      },
    ],
  ]);

  /** What a finished conversion knows about the file: its shape from its name, on this desk. */
  function converted(video: ArticleVideo): ArticleVideo {
    if (options.failWith !== undefined) {
      return { ...video, state: 'failed', failureCode: options.failWith };
    }
    const vertical = (video.fileName ?? '').toLowerCase().includes('vertical');
    return {
      ...video,
      state: 'ready',
      failureCode: null,
      orientation: vertical ? 'vertical' : 'horizontal',
      durationMs: 252_000,
      widthPx: vertical ? 1_080 : 1_920,
      heightPx: vertical ? 1_920 : 1_080,
    };
  }

  function withAddresses(articleId: string, video: ArticleVideo): ArticleVideo {
    if (video.source !== 'upload' || video.state !== 'ready') {
      return { ...video, playbackUrl: null, frameUrls: [] };
    }
    const vertical = video.orientation === 'vertical';
    return {
      ...video,
      playbackUrl: `https://storage.googleapis.com/moonx-fixture/videos/${articleId}/playback.mp4`,
      frameUrls: Array.from({ length: FRAME_COUNT }, (_, index) => frameUrl(index, vertical)),
    };
  }

  return {
    current: (articleId) => held.get(articleId)?.video ?? null,

    read: (articleId) => {
      const found = held.get(articleId);
      if (found === undefined) {
        throw refusal(404, 'NOT_FOUND', 'No such video.');
      }
      const { video } = found;
      const next =
        video.state === 'uploaded'
          ? { ...video, state: 'processing' as const }
          : video.state === 'processing'
            ? converted(video)
            : video;
      held.set(articleId, { video: next });
      return withAddresses(articleId, next);
    },

    set: (articleId, body) => {
      if (body.source === 'youtube') {
        const link = youtubeLink(body.url);
        if (link === null) {
          throw refusal(422, 'UNPROCESSABLE_UNSPECIFIED', 'not a link to one YouTube video');
        }
        const video: ArticleVideo = {
          source: 'youtube',
          state: 'ready',
          failureCode: null,
          youtubeVideoId: link.videoId,
          orientation: body.orientation ?? (link.isShort ? 'vertical' : 'horizontal'),
          durationMs: null,
          widthPx: null,
          heightPx: null,
          fileName: null,
          byteSize: null,
        };
        held.set(articleId, { video });
        return withAddresses(articleId, video);
      }
      if (body.byteSize >= VIDEO_MAX_BYTES) {
        throw refusal(422, 'UPLOAD_TOO_LARGE', 'a video file may be at most 3 GB');
      }
      const video: ArticleVideo = {
        source: 'upload',
        state: 'uploaded',
        failureCode: null,
        youtubeVideoId: null,
        orientation: null,
        durationMs: null,
        widthPx: null,
        heightPx: null,
        fileName: body.fileName,
        byteSize: body.byteSize,
      };
      held.set(articleId, { video });
      return withAddresses(articleId, video);
    },

    remove: (articleId) => {
      held.delete(articleId);
    },

    cover: (articleId, body) => {
      const video = held.get(articleId)?.video;
      const stem = articleId.slice(-8);
      if ('frame' in body) {
        if (video?.source !== 'upload' || video.state !== 'ready' || body.frame >= FRAME_COUNT) {
          throw refusal(422, 'UNPROCESSABLE_UNSPECIFIED', 'this video has no such frame');
        }
        const vertical = video.orientation === 'vertical';
        return {
          fileId: `img_frame_${stem}_${String(body.frame)}`,
          url: frameUrl(body.frame, vertical),
          widthPx: vertical ? 1_080 : 1_920,
          heightPx: vertical ? 1_920 : 1_080,
          byteSize: 180_000,
          contentType: 'image/jpeg',
        };
      }
      if (video?.source !== 'youtube' || video.youtubeVideoId === null) {
        throw refusal(422, 'UNPROCESSABLE_UNSPECIFIED', 'this video is not a YouTube link');
      }
      return {
        fileId: `img_still_${stem}`,
        url: `https://i.ytimg.com/vi/${video.youtubeVideoId ?? ''}/hqdefault.jpg`,
        widthPx: 1_280,
        heightPx: 720,
        byteSize: 96_400,
        contentType: 'image/jpeg',
      };
    },
  };
}
